import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { 
  TrendingUp, 
  TrendingDown,
  Footprints,
  Apple,
  Scale,
  Flame,
  Target,
  BarChart3,
  Ruler,
  Clock,
  Pill,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { useTheme } from './ThemeProvider';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, BarChart, Bar } from 'recharts';
import { StatisticsSkeleton } from './Skeletons';

import api from '../services/api';

// Custom Tooltip
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-slate-800 border border-slate-600 rounded-lg p-3 shadow-lg">
        <p className="text-slate-300 text-sm mb-1">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: <span className="font-bold">{entry.value?.toLocaleString()}</span>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export default function Statistics({ onBack, isExpired, onNavigate }) {
  const { theme } = useTheme();
  const [activeTab, setActiveTab] = useState('nutrition');
  const [timeRange, setTimeRange] = useState('week');
  const [nutritionData, setNutritionData] = useState([]);
  const [walkingData, setWalkingData] = useState([]);
  const [weightData, setWeightData] = useState([]);
  const [waistData, setWaistData] = useState([]);
  const [adherenceData, setAdherenceData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const medsEnabled = localStorage.getItem('medications_enabled') !== 'false';

  // Load data on mount
  useEffect(() => {
    const loadAllData = async () => {
      setIsLoading(true);
      loadNutritionData();
      loadWalkingData();
      loadWeightData();
      loadWaistData();
      if (medsEnabled) loadAdherenceData();
      // Small delay for smooth skeleton transition
      setTimeout(() => setIsLoading(false), 300);
    };
    loadAllData();
  }, [timeRange]);

  const loadWaistData = () => {
    const history = JSON.parse(localStorage.getItem('gym_notebook_waist_history') || '[]');
    
    const data = history
      .slice(0, 30)
      .reverse()
      .map(entry => ({
        date: new Date(entry.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        waist: entry.waist,
        unit: entry.unit
      }));
    
    setWaistData(data);
  };

  const loadAdherenceData = async () => {
    try {
      const days = timeRange === 'week' ? 7 : timeRange === 'month' ? 30 : 90;
      const data = await api.get(`/medications/adherence?days=${days}`);
      // api.get() returns parsed JSON directly, not a Response object
      if (data && data.adherence_pct !== undefined) {
        // Format dates for chart
        if (data.daily) {
          data.daily = data.daily.map(entry => ({
            ...entry,
            date: new Date(entry.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          }));
        }
        setAdherenceData(data);
      }
    } catch (e) {
      console.error('Error loading adherence data:', e);
    }
  };

  const loadNutritionData = () => {
    const days = timeRange === 'week' ? 7 : timeRange === 'month' ? 30 : 90;
    const data = [];
    
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      
      // Load from localStorage
      const dayData = JSON.parse(localStorage.getItem(`nutrition_${dateStr}`) || '{}');
      
      data.push({
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        calories: dayData.calories || 0,
        protein: dayData.protein || 0,
        carbs: dayData.carbs || 0,
        fat: dayData.fat || 0
      });
    }
    
    setNutritionData(data);
  };

  const loadWalkingData = () => {
    const days = timeRange === 'week' ? 7 : timeRange === 'month' ? 30 : 90;
    const data = [];
    const walkingDaily = JSON.parse(localStorage.getItem('gym_notebook_walking_daily_data') || '{}');
    
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      
      const dayData = walkingDaily[dateStr] || {};
      
      data.push({
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        steps: dayData.steps || 0,
        distance: dayData.distance || 0,
        calories: dayData.calories || 0
      });
    }
    
    setWalkingData(data);
  };

  const loadWeightData = () => {
    const historyKey = 'gym_notebook_body_measurements_history';
    const history = JSON.parse(localStorage.getItem(historyKey) || '[]');
    
    const data = history
      .filter(entry => entry.bodyweight)
      .slice(0, 30)
      .reverse()
      .map(entry => ({
        date: new Date(entry.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        weight: parseFloat(entry.bodyweight)
      }));
    
    setWeightData(data);
  };

  // Calculate totals
  const totalCalories = nutritionData.reduce((sum, d) => sum + d.calories, 0);
  const avgCalories = nutritionData.length > 0 ? Math.round(totalCalories / nutritionData.filter(d => d.calories > 0).length) || 0 : 0;
  const totalSteps = walkingData.reduce((sum, d) => sum + d.steps, 0);
  const avgSteps = walkingData.length > 0 ? Math.round(totalSteps / walkingData.filter(d => d.steps > 0).length) || 0 : 0;

  // Show skeleton while loading
  if (isLoading) {
    return <StatisticsSkeleton />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex flex-col">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-slate-950 ios-header border-none" style={{ paddingTop: 'max(16px, env(safe-area-inset-top, 16px))', border: 'none' }}>
        <div className="container mx-auto px-4 py-3 flex items-center">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg shadow-violet-500/20">
              <BarChart3 className="h-5 w-5 text-white" />
            </div>
            <h1 className="text-lg font-heading font-bold text-white">
              Statistics
            </h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 container mx-auto px-4 py-6 pb-8 pt-20">
        <div className="max-w-lg mx-auto">
        {/* Time Range Selector */}
        <div className="flex gap-2 mb-6">
          {['week', 'month', '3months'].map((range) => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`flex-1 py-2.5 px-3 rounded-xl text-sm font-medium transition-all ${
                timeRange === range
                  ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/20'
                  : 'bg-slate-900 text-slate-400 hover:bg-slate-800 border border-slate-800'
              }`}
            >
              {range === 'week' ? '7 Days' : range === 'month' ? '30 Days' : '90 Days'}
            </button>
          ))}
        </div>

        {/* Tab Selector */}
        <div className="flex gap-1 mb-6 p-1.5 bg-slate-900/80 rounded-2xl border border-slate-800">
          <button
            onClick={() => setActiveTab('nutrition')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl transition-all ${
              activeTab === 'nutrition'
                ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                : 'text-slate-400 hover:text-white hover:bg-slate-800'
            }`}
          >
            <Apple className="h-4 w-4" />
            <span className="text-xs font-medium">Nutrition</span>
          </button>
          <button
            onClick={() => setActiveTab('walking')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl transition-all ${
              activeTab === 'walking'
                ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                : 'text-slate-400 hover:text-white hover:bg-slate-800'
            }`}
          >
            <Footprints className="h-4 w-4" />
            <span className="text-xs font-medium">Walking</span>
          </button>
          <button
            onClick={() => setActiveTab('weight')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl transition-all ${
              activeTab === 'weight'
                ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                : 'text-slate-400 hover:text-white hover:bg-slate-800'
            }`}
          >
            <Scale className="h-4 w-4" />
            <span className="text-xs font-medium">Weight</span>
          </button>
          <button
            onClick={() => setActiveTab('waist')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl transition-all ${
              activeTab === 'waist'
                ? 'bg-violet-500/20 text-violet-400 border border-violet-500/30'
                : 'text-slate-400 hover:text-white hover:bg-slate-800'
            }`}
          >
            <Ruler className="h-4 w-4" />
            <span className="text-xs font-medium">Waist</span>
          </button>
          {medsEnabled && (
            <button
              onClick={() => setActiveTab('meds')}
              data-testid="stats-meds-tab"
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl transition-all ${
                activeTab === 'meds'
                  ? 'bg-teal-500/20 text-teal-400 border border-teal-500/30'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
            >
              <Pill className="h-4 w-4" />
              <span className="text-xs font-medium">Meds</span>
            </button>
          )}
        </div>

        {/* Nutrition Stats */}
        {activeTab === 'nutrition' && (
          <div className="space-y-4">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 gap-3">
              <Card className="bg-slate-900/80 border-slate-800 rounded-2xl">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center">
                      <Flame className="h-3.5 w-3.5 text-white" />
                    </div>
                    <span className="text-slate-400 text-sm">Avg Calories</span>
                  </div>
                  <p className="text-2xl font-bold text-white">{avgCalories.toLocaleString()}</p>
                  <p className="text-xs text-slate-500">kcal/day</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-900/80 border-slate-800 rounded-2xl">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
                      <Target className="h-3.5 w-3.5 text-white" />
                    </div>
                    <span className="text-slate-400 text-sm">Total Calories</span>
                  </div>
                  <p className="text-2xl font-bold text-white">{totalCalories.toLocaleString()}</p>
                  <p className="text-xs text-slate-500">kcal total</p>
                </CardContent>
              </Card>
            </div>

            {/* Calories Chart */}
            <Card className="bg-slate-900/80 border-slate-800 rounded-2xl">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center">
                    <Flame className="h-4 w-4 text-white" />
                  </div>
                  Daily Calories
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="99%" height={256}>
                    <AreaChart data={nutritionData}>
                      <defs>
                        <linearGradient id="caloriesGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#f97316" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#f97316" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis dataKey="date" stroke="#9ca3af" tick={{ fontSize: 10 }} />
                      <YAxis stroke="#9ca3af" tick={{ fontSize: 10 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Area type="monotone" dataKey="calories" name="Calories" stroke="#f97316" fill="url(#caloriesGradient)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Macros Chart */}
            <Card className="bg-slate-900/80 border-slate-800 rounded-2xl">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-500 to-violet-600 flex items-center justify-center">
                    <BarChart3 className="h-4 w-4 text-white" />
                  </div>
                  Macronutrients
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-48">
                  <ResponsiveContainer width="99%" height={192}>
                    <BarChart data={nutritionData.slice(-7)}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis dataKey="date" stroke="#9ca3af" tick={{ fontSize: 10 }} />
                      <YAxis stroke="#9ca3af" tick={{ fontSize: 10 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="protein" name="Protein" fill="#22c55e" />
                      <Bar dataKey="carbs" name="Carbs" fill="#3b82f6" />
                      <Bar dataKey="fat" name="Fat" fill="#eab308" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex justify-center gap-6 mt-3">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    <span className="text-xs text-slate-400">Protein</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                    <span className="text-xs text-slate-400">Carbs</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <span className="text-xs text-slate-400">Fat</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Walking Stats (Combined Steps + Walking) */}
        {activeTab === 'walking' && (
          <div className="space-y-4">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 gap-3">
              <Card className="bg-slate-900/80 border-slate-800 rounded-2xl">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center">
                      <Footprints className="h-3.5 w-3.5 text-white" />
                    </div>
                    <span className="text-slate-400 text-sm">Avg Steps</span>
                  </div>
                  <p className="text-2xl font-bold text-white">{avgSteps.toLocaleString()}</p>
                  <p className="text-xs text-slate-500">steps/day</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-900/80 border-slate-800 rounded-2xl">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
                      <Target className="h-3.5 w-3.5 text-white" />
                    </div>
                    <span className="text-slate-400 text-sm">Total Steps</span>
                  </div>
                  <p className="text-2xl font-bold text-white">{totalSteps.toLocaleString()}</p>
                  <p className="text-xs text-slate-500">steps total</p>
                </CardContent>
              </Card>
            </div>

            {/* Daily Goal Progress */}
            <Card className="bg-slate-900/80 border-slate-800 rounded-2xl">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center">
                    <Target className="h-4 w-4 text-white" />
                  </div>
                  Daily Goal (10,000 steps)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {walkingData.slice(-7).map((day, index) => {
                    const goalPercent = Math.min(100, (day.steps / 10000) * 100);
                    const isGoalMet = day.steps >= 10000;
                    return (
                      <div key={index} className="flex items-center gap-3">
                        <span className="text-xs text-slate-400 w-16">{day.date}</span>
                        <div className="flex-1 h-4 bg-slate-700 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full transition-all ${isGoalMet ? 'bg-green-500' : 'bg-blue-500'}`}
                            style={{ width: `${goalPercent}%` }}
                          />
                        </div>
                        <span className={`text-xs font-medium w-16 text-right ${isGoalMet ? 'text-green-400' : 'text-slate-400'}`}>
                          {day.steps >= 1000 ? `${(day.steps / 1000).toFixed(1)}k` : day.steps}
                        </span>
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-slate-500 mt-3 text-center">
                  Days goal met: {walkingData.filter(d => d.steps >= 10000).length} / {walkingData.filter(d => d.steps > 0).length || walkingData.length}
                </p>
              </CardContent>
            </Card>

            {/* Steps Trend Chart */}
            <Card className="bg-slate-900/80 border-slate-800 rounded-2xl">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center">
                    <Footprints className="h-4 w-4 text-white" />
                  </div>
                  Steps Trend
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-48">
                  <ResponsiveContainer width="99%" height={192}>
                    <AreaChart data={walkingData}>
                      <defs>
                        <linearGradient id="stepsGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis dataKey="date" stroke="#9ca3af" tick={{ fontSize: 10 }} />
                      <YAxis stroke="#9ca3af" tick={{ fontSize: 10 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Area type="monotone" dataKey="steps" name="Steps" stroke="#3b82f6" fill="url(#stepsGradient)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Calories Burned Chart */}
            <Card className="bg-slate-900/80 border-slate-800 rounded-2xl">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center">
                    <Flame className="h-4 w-4 text-white" />
                  </div>
                  Calories Burned
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-40">
                  <ResponsiveContainer width="99%" height={160}>
                    <BarChart data={walkingData.slice(-7)}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis dataKey="date" stroke="#9ca3af" tick={{ fontSize: 10 }} />
                      <YAxis stroke="#9ca3af" tick={{ fontSize: 10 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="calories" name="Calories" fill="#f97316" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Weight Stats */}
        {activeTab === 'weight' && (
          <div className="space-y-4">
            {/* Weight Change */}
            {weightData.length >= 2 && (
              <Card className="bg-slate-900/80 border-slate-800 rounded-2xl">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-slate-400 text-sm mb-1">Weight Change</p>
                      <p className="text-2xl font-bold text-white">
                        {(weightData[weightData.length - 1]?.weight - weightData[0]?.weight).toFixed(1)} kg
                      </p>
                    </div>
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      weightData[weightData.length - 1]?.weight < weightData[0]?.weight 
                        ? 'bg-gradient-to-br from-green-500 to-emerald-600' 
                        : 'bg-gradient-to-br from-red-500 to-rose-600'
                    }`}>
                      {weightData[weightData.length - 1]?.weight < weightData[0]?.weight 
                        ? <TrendingDown className="h-6 w-6 text-white" />
                        : <TrendingUp className="h-6 w-6 text-white" />
                      }
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Weight Chart */}
            <Card className="bg-slate-900/80 border-slate-800 rounded-2xl">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
                    <Scale className="h-4 w-4 text-white" />
                  </div>
                  Weight Trend
                </CardTitle>
              </CardHeader>
              <CardContent>
                {weightData.length > 0 ? (
                  <div className="h-64">
                    <ResponsiveContainer width="99%" height={256}>
                      <LineChart data={weightData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis dataKey="date" stroke="#9ca3af" tick={{ fontSize: 10 }} />
                        <YAxis stroke="#9ca3af" tick={{ fontSize: 10 }} domain={['dataMin - 2', 'dataMax + 2']} />
                        <Tooltip content={<CustomTooltip />} />
                        <Line 
                          type="monotone" 
                          dataKey="weight" 
                          name="Weight (kg)" 
                          stroke="#22c55e" 
                          strokeWidth={2}
                          dot={{ fill: '#22c55e', strokeWidth: 2 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-64 flex items-center justify-center">
                    <div className="text-center">
                      <Scale className="h-12 w-12 text-slate-600 mx-auto mb-3" />
                      <p className="text-slate-400">No weight data yet</p>
                      <p className="text-slate-500 text-sm">Log your weight in Personal → Body Info</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Waist Stats */}
        {activeTab === 'waist' && (
          <div className="space-y-4">
            {/* Waist Change */}
            {waistData.length >= 2 && (
              <Card className="bg-slate-900/80 border-slate-800 rounded-2xl">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-slate-400 text-sm mb-1">Waist Change</p>
                      <p className="text-2xl font-bold text-white">
                        {(waistData[waistData.length - 1]?.waist - waistData[0]?.waist).toFixed(1)} {waistData[0]?.unit || 'cm'}
                      </p>
                    </div>
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      waistData[waistData.length - 1]?.waist < waistData[0]?.waist 
                        ? 'bg-gradient-to-br from-green-500 to-emerald-600' 
                        : 'bg-gradient-to-br from-red-500 to-rose-600'
                    }`}>
                      {waistData[waistData.length - 1]?.waist < waistData[0]?.waist 
                        ? <TrendingDown className="h-6 w-6 text-white" />
                        : <TrendingUp className="h-6 w-6 text-red-400" />
                      }
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Waist Chart */}
            <Card className="bg-slate-900/80 border-slate-800 rounded-2xl">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                    <Ruler className="h-4 w-4 text-white" />
                  </div>
                  Waist Measurement Trend
                </CardTitle>
              </CardHeader>
              <CardContent>
                {waistData.length > 0 ? (
                  <div className="h-64">
                    <ResponsiveContainer width="99%" height={256}>
                      <LineChart data={waistData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis dataKey="date" stroke="#9ca3af" tick={{ fontSize: 10 }} />
                        <YAxis stroke="#9ca3af" tick={{ fontSize: 10 }} domain={['dataMin - 2', 'dataMax + 2']} />
                        <Tooltip content={<CustomTooltip />} />
                        <Line 
                          type="monotone" 
                          dataKey="waist" 
                          name="Waist" 
                          stroke="#a855f7" 
                          strokeWidth={2}
                          dot={{ fill: '#a855f7', strokeWidth: 2 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-64 flex items-center justify-center">
                    <div className="text-center">
                      <Ruler className="h-12 w-12 text-slate-600 mx-auto mb-3" />
                      <p className="text-slate-400">No waist data yet</p>
                      <p className="text-slate-500 text-sm">Log your waist in Personal → Body Info</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Waist History */}
            {waistData.length > 0 && (
              <Card className="bg-slate-900/80 border-slate-800 rounded-2xl">
                <CardHeader className="pb-2">
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                      <Clock className="h-4 w-4 text-white" />
                    </div>
                    Recent Measurements
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {waistData.slice(-10).reverse().map((entry, index) => (
                      <div key={index} className="flex items-center justify-between py-2 border-b border-slate-700/50 last:border-0">
                        <span className="text-slate-400 text-sm">{entry.date}</span>
                        <span className="text-white font-medium">{entry.waist} {entry.unit || 'cm'}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Medication Adherence Stats */}
        {activeTab === 'meds' && (
          <div className="space-y-4" data-testid="stats-meds-content">
            {/* Adherence Score */}
            <Card className="bg-slate-900/80 border-slate-800 rounded-2xl">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center">
                      <Pill className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <p className="text-slate-400 text-sm">Adherence Score</p>
                      <p className="text-3xl font-bold text-white">{adherenceData?.adherence_pct || 0}%</p>
                    </div>
                  </div>
                  <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                    (adherenceData?.adherence_pct || 0) >= 80 ? 'bg-emerald-500/20 text-emerald-400' :
                    (adherenceData?.adherence_pct || 0) >= 50 ? 'bg-amber-500/20 text-amber-400' :
                    'bg-red-500/20 text-red-400'
                  }`}>
                    {(adherenceData?.adherence_pct || 0) >= 80 ? 'Great' :
                     (adherenceData?.adherence_pct || 0) >= 50 ? 'Needs Improvement' : 'Low'}
                  </div>
                </div>
                {/* Progress bar */}
                <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-teal-500 to-emerald-400 transition-all duration-700"
                    style={{ width: `${Math.min(100, adherenceData?.adherence_pct || 0)}%` }}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Summary Cards */}
            <div className="grid grid-cols-3 gap-3">
              <Card className="bg-slate-900/80 border-slate-800 rounded-2xl">
                <CardContent className="p-4 text-center">
                  <CheckCircle className="h-5 w-5 text-emerald-400 mx-auto mb-1" />
                  <div className="text-xl font-bold text-white">{adherenceData?.taken || 0}</div>
                  <div className="text-xs text-slate-400">Taken</div>
                </CardContent>
              </Card>
              <Card className="bg-slate-900/80 border-slate-800 rounded-2xl">
                <CardContent className="p-4 text-center">
                  <XCircle className="h-5 w-5 text-red-400 mx-auto mb-1" />
                  <div className="text-xl font-bold text-white">{adherenceData?.skipped || 0}</div>
                  <div className="text-xs text-slate-400">Skipped</div>
                </CardContent>
              </Card>
              <Card className="bg-slate-900/80 border-slate-800 rounded-2xl">
                <CardContent className="p-4 text-center">
                  <Clock className="h-5 w-5 text-amber-400 mx-auto mb-1" />
                  <div className="text-xl font-bold text-white">{adherenceData?.missed || 0}</div>
                  <div className="text-xs text-slate-400">Missed</div>
                </CardContent>
              </Card>
            </div>

            {/* Daily Adherence Chart */}
            {adherenceData?.daily?.length > 0 && (
              <Card className="bg-slate-900/80 border-slate-800 rounded-2xl">
                <CardHeader className="pb-2">
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center">
                      <BarChart3 className="h-4 w-4 text-white" />
                    </div>
                    Daily Doses
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-48">
                    <ResponsiveContainer width="99%" height={192}>
                      <BarChart data={adherenceData.daily}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="date" tick={{ fill: '#94a3b8', fontSize: 11 }} tickLine={false} />
                        <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} tickLine={false} allowDecimals={false} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="taken" stackId="a" fill="#10b981" radius={[0, 0, 0, 0]} name="Taken" />
                        <Bar dataKey="skipped" stackId="a" fill="#ef4444" radius={[4, 4, 0, 0]} name="Skipped" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Per-Medication Breakdown */}
            {adherenceData?.per_med?.length > 0 && (
              <Card className="bg-slate-900/80 border-slate-800 rounded-2xl">
                <CardHeader className="pb-2">
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center">
                      <Pill className="h-4 w-4 text-white" />
                    </div>
                    Per Medication
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {adherenceData.per_med.map((med, idx) => (
                      <div key={idx} className="space-y-1.5" data-testid={`adherence-med-${idx}`}>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-white font-medium">{med.name}</span>
                          <span className={`text-sm font-bold ${
                            med.adherence_pct >= 80 ? 'text-emerald-400' :
                            med.adherence_pct >= 50 ? 'text-amber-400' : 'text-red-400'
                          }`}>
                            {med.adherence_pct}%
                          </span>
                        </div>
                        <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${
                              med.adherence_pct >= 80 ? 'bg-emerald-500' :
                              med.adherence_pct >= 50 ? 'bg-amber-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${Math.min(100, med.adherence_pct)}%` }}
                          />
                        </div>
                        <div className="flex gap-3 text-xs text-slate-500">
                          <span>{med.taken} taken</span>
                          <span>{med.skipped} skipped</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Empty state */}
            {(!adherenceData || adherenceData.total_expected === 0) && (
              <Card className="bg-slate-900/80 border-slate-800 rounded-2xl">
                <CardContent className="p-8 text-center">
                  <Pill className="h-10 w-10 text-slate-600 mx-auto mb-3" />
                  <p className="text-white font-medium mb-1">No medication data yet</p>
                  <p className="text-slate-500 text-sm">Add medications and log your doses to see adherence stats here.</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}
        </div>
      </main>
    </div>
  );
}
